const cooperators = {
  id: "cooperators",
  label: "Co-operators",
  tabDescription: "Establishing Enterprise Design Capability",
  title: "Establishing Enterprise Design Capability",
  sections: [
    {
      id: "mandate",
      title: "Mandate",
      paragraphs: [
        "Co-operators was investing in customer-facing platforms, internal applications, and a major corporate rebrand, but design capability remained fragmented across teams, products, vendors, and delivery practices.",
        "UX expertise was distributed across teams, research and testing were inconsistent, accessibility maturity varied by initiative, and the corporate brand had not yet been translated into practical digital guidance. Teams frequently solved similar problems independently without shared standards, governance, or reusable foundations.",
        "I was brought in to establish an in-house multidisciplinary UX practice while supporting major initiatives already underway. The mandate was to grow internal expertise, introduce UX benchmarking, embed accessibility into delivery, create the Oak design system and digital standards, and establish the governance needed to improve consistency across customer, web, and internal platforms.",
        "The capability had to be built while the work continued.",
      ],
    },
    {
      id: "strategy",
      title: "Strategy",
      paragraphs: [
        "Rather than treating each initiative as a separate project, I approached the work as an enterprise capability transformation. Every decision was intended to strengthen how the organization designed, delivered, and governed digital products, not just improve an individual experience.",
        "I treated design systems, accessibility, research, governance, and standards as one shared operating model rather than separate initiatives, each reinforcing the others instead of competing for attention. That meant creating the Oak design system and digital style guide, translating new brand standards into practical digital guidance, then carrying that foundation through delivery—from redesigning the client portal to rebuilding the enterprise website on Sitecore. Alongside the systems work, I grew the practice's own designers into leaders, scaling the team to more than 15 people across the areas they supported.",
        "The objective wasn't simply greater consistency. It was creating an environment where teams spent less time reinventing solutions and more time solving meaningful customer and business problems, while giving the organization a stronger capability to evolve long after the individual projects were complete.",
      ],
    },
  ],
  capabilities: [
    {
      id: "design-leadership-team-development",
      title: "Design Leadership & Team Development",
      description:
        "Built and scaled an in-house multidisciplinary UX practice spanning product design, research, accessibility, and design systems while strengthening collaboration across Product, Technology, Brand, QA, and business teams.",
      contributions: [
        "Established and led an enterprise multidisciplinary UX practice.",
        "Embedded design leadership across multiple product teams and strategic initiatives.",
        "Grew designers through mentoring, expanded ownership, and leadership opportunities.",
        "Positioned UX as a strategic partner in product planning and digital delivery.",
      ],
    },
    {
      id: "design-systems-accessibility",
      title: "Design Systems & Accessibility",
      description:
        "Established scalable enterprise foundations for consistent, accessible digital delivery through reusable components, governance, documentation, digital standards, and shared implementation practices.",
      contributions: [
        "Led the creation of Oak, a roughly $2 million enterprise design-system investment supporting React, Vue, and Angular.",
        "Embedded accessibility into components, design standards, engineering workflows, and QA rather than treating it as a final compliance review.",
        "Introduced governance, release management, feature requests, contribution practices, and documentation.",
        "Translated the corporate rebrand into accessible digital guidance for light and dark experiences.",
      ],
    },
    {
      id: "ux-testing-strategy",
      title: "UX Testing & Strategy",
      description:
        "Embedded evidence-based evaluation practices that enabled product decisions to be driven by customer insight rather than assumption or individual stakeholder preference.",
      contributions: [
        "Introduced UX benchmarking to establish experience baselines and identify priority gaps.",
        "Expanded usability testing across active products and platforms.",
        "Connected research, analytics, customer needs, and business priorities to product direction.",
        "Used evidence to support prioritization and more confident cross-functional decisions.",
      ],
    },
    {
      id: "governance-scalable-delivery",
      title: "Governance & Scalable Delivery",
      description:
        "Created governance practices that aligned teams, standards, and delivery while enabling consistency without creating centralized bottlenecks.",
      contributions: [
        "Established shared documentation, intake, feedback, and decision-making practices.",
        "Introduced release management and contribution models for Oak.",
        "Connected Design, Engineering, Brand, Accessibility, and QA around common expectations.",
        "Reduced repeated design and implementation work by creating reliable foundations teams could reuse.",
      ],
    },
  ],
  outcomes: [
    {
      title: "Enterprise Design Capability",
      description:
        "Established an in-house multidisciplinary UX practice with the leadership, operating model, and cross-functional partnerships needed to support digital products and platforms across the enterprise.",
    },
    {
      title: "Consistent, Accessible Digital Experiences",
      description:
        "Created enterprise design standards, reusable components, accessibility practices, and governance that enabled teams to deliver more consistent, inclusive digital experiences while reducing implementation complexity across products and platforms.",
    },
    {
      title: "Faster, More Predictable Delivery",
      description:
        "Accelerated design and front-end delivery through reusable components, shared standards, and established implementation patterns, allowing teams to deliver new products and enhancements with greater speed, consistency, and confidence.",
    },
    {
      title: "Better Product Decisions",
      description:
        "Introduced UX benchmarking, usability testing, and evidence-based evaluation practices that helped teams identify experience gaps, prioritize investments, and make more informed product decisions.",
    },
    {
      title: "Built for Long-Term Scale",
      description:
        "Created sustainable foundations—including design systems, governance, research practices, accessibility, and shared standards—that enabled new products, platforms, and teams to build on proven capabilities rather than repeatedly starting from scratch.",
    },
  ],
  reflection: {
    quote:
      "Organizations rarely struggle because people lack talent. More often, capable people are working without shared direction, reliable systems, or the evidence needed to make confident decisions.",
    supporting:
      "Although Oak became the most visible outcome, the larger achievement was creating the operating model around it—people, governance, research, accessibility, standards, and cross-functional relationships that enabled better digital products long after the initial projects were delivered.",
  },
};

export default cooperators;
